// Entrypoint for the lightweight package-check page (package.html).
//
// Accepts a raw distribution file — .mcaddon / .mcworld / .mctemplate /
// .mcpack — and validates it against the package rule set (every Marketplace
// rule except the Store Art / Marketing Art checks). The upload + report
// rendering reuse the same machinery as the full validator; the differences
// are the accepted file types, the /package/* endpoints, and the absence of
// the auto-fixer (which targets the assembled submission zip layout).

import {
  createSharedReport,
  validatePackage,
  type ValidateResponse,
} from "./api.js";
import { initTheme, initVersionFooter } from "./chrome.js";
import { openShareModal } from "./share.js";
import {
  formatBytes,
  renderReport,
  renderScanning,
  renderUpload,
  type ScanningState,
} from "./render.js";
import { downloadReportPdf } from "./report-pdf.js";

const stage = document.getElementById("stage") as HTMLElement;

const MAX_BYTES = 500 * 1024 * 1024;

// Accepted raw distribution extensions. Kept in sync with the worker's
// loosePackageKindForFilename (shared/src/loose-package.ts). Duplicated here
// (rather than imported from @waypoint/validator-core) so the browser bundle
// doesn't pull in the whole rule engine + spellcheck dictionary just for a
// filename check.
const SUPPORTED_EXTENSIONS = new Set(["mcaddon", "mcworld", "mctemplate", "mctemplates", "mcpack"]);
const LOOSE_PACKAGE_ACCEPT = ".mcaddon,.mcworld,.mctemplate,.mcpack";

function isSupportedPackage(filename: string): boolean {
  const m = /\.([a-z0-9]+)\s*$/i.exec(filename.trim());
  return m ? SUPPORTED_EXTENSIONS.has(m[1]!.toLowerCase()) : false;
}

const UPLOAD_COPY = {
  accept: LOOSE_PACKAGE_ACCEPT,
  heading: "Drop your package to check",
  subHtml:
    'Supports <span class="mono">.mcaddon</span>, <span class="mono">.mcworld</span>, ' +
    '<span class="mono">.mctemplate</span>, and <span class="mono">.mcpack</span> up to 500&nbsp;MB. ' +
    "Store Art &amp; Marketing Art checks are skipped.",
};

initTheme();
initVersionFooter();
showUpload();

function showUpload(): void {
  renderUpload(stage, { onFile: (file) => void handleFile(file) }, UPLOAD_COPY);
}

async function handleFile(file: File): Promise<void> {
  if (!isSupportedPackage(file.name)) {
    return showError(
      `"${file.name}" isn't a supported package. Use .mcaddon, .mcworld, .mctemplate, or .mcpack.`,
    );
  }
  if (file.size === 0) {
    return showError("That file is empty.");
  }
  if (file.size > MAX_BYTES) {
    return showError(
      `File is ${formatBytes(file.size)}; limit is ${formatBytes(MAX_BYTES)}.`,
    );
  }

  const startedAt = performance.now();
  let state: ScanningState = {
    fileName: file.name,
    progress: 2,
    label: "Hashing",
    lines: [`Computing SHA-256 for ${file.name} (${formatBytes(file.size)})`],
  };
  renderScanning(stage, state);

  const shaPromise = sha256Hex(file);

  const progressTick = (label: string, progress: number, line: string): void => {
    state = { fileName: file.name, progress, label, lines: [...state.lines, line] };
    renderScanning(stage, state);
  };

  try {
    progressTick("Preparing upload", 6, "Requesting presigned upload URL");

    const data = await validatePackage(file, {
      onStatus: (label) => {
        if (label.toLowerCase().startsWith("uploading")) return;
        if (label === "Validating") {
          progressTick("Running checks", 92, "Worker is validating the package");
        } else {
          progressTick(label.toUpperCase(), Math.min(state.progress + 4, 90), label);
        }
      },
      onUploadProgress: (pct) => {
        const mapped = 10 + Math.round(pct * 0.75);
        state = {
          fileName: file.name,
          progress: mapped,
          label: "Uploading",
          lines: state.lines,
        };
        renderScanning(stage, state);
      },
    });

    const sha = await shaPromise;
    const durationMs = performance.now() - startedAt;

    renderReport(
      stage,
      { response: data, sha256: sha, durationMs },
      {
        onReset: () => showUpload(),
        onShare: () => void runShare({ response: data, sha256: sha, durationMs }),
        onDownloadPdf: () => downloadReportPdf({ response: data, sha256: sha, durationMs }),
      },
    );

    stage.scrollIntoView({ behavior: "smooth", block: "start" });
  } catch (err) {
    showError((err as Error).message);
  }
}

async function runShare(input: {
  response: ValidateResponse;
  sha256: string;
  durationMs: number;
}): Promise<void> {
  showBanner("Creating share link…", "info");
  try {
    const { id, expiresAt } = await createSharedReport({
      report: input.response.report,
      ruleSet: input.response.ruleSet,
      file: input.response.file,
      sha256: input.sha256,
      durationMs: input.durationMs,
    });
    const url = `${window.location.origin}/report/${id}`;
    openShareModal(url, expiresAt);
    stage.querySelector(".status-banner")?.remove();
  } catch (err) {
    showError(`Couldn't create share link: ${(err as Error).message}`);
  }
}

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function showError(msg: string): void {
  showUpload();
  const banner = document.createElement("div");
  banner.className = "status-banner";
  banner.textContent = msg;
  stage.append(banner);
}

function showBanner(msg: string, tone: "info" | "error" = "info"): void {
  const existing = stage.querySelector(".status-banner");
  if (existing) existing.remove();
  const banner = document.createElement("div");
  banner.className = `status-banner ${tone === "info" ? "info" : ""}`.trim();
  banner.textContent = msg;
  stage.append(banner);
}
